import { Call, RpcProvider, addAddressPadding } from 'starknet';
import { getConfigManager } from '../../config';
import { MultiCallContract } from '../../services';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import { Decimal } from '../../datastructures';
import { Nullable } from '../../interfaces';
import avnuSupportedTokensAtom, { AVNUTokenConfig } from '../swap/avnuSupportedTokensAtom';
import walletAddressAtom from './walletAddressAtom';
import { createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';

interface TokenToUpdateData {
  address: string;
  decimals: number;
}

const walletTokenBalancesAtom = createAsyncAtom<{ [tokenAddress: string]: Decimal }>(
  async get => {
    const walletAddress = await get(walletAddressAtom);
    const avnuSupportedTokens = await get(avnuSupportedTokensAtom);
    const provider = await get(rpcProviderAtom);

    const tokenBalancesMap = await fetchData(provider, walletAddress, avnuSupportedTokens);

    return tokenBalancesMap;
  },
  30000,
  [
    'lend-and-borrow-deposit',
    'lend-and-borrow-withdraw',
    'lend-and-borrow-borrow',
    'lend-and-borrow-repay',
    'swap',
    'stake',
    'unstake',
    'unstake-and-deposit',
    'pool-deposit',
    'pool-withdraw',
    'claim-rewards',
    'claim-airdrop',
    'user-refresh',
  ],
);

async function fetchData(provider: RpcProvider, walletAddress: Nullable<string>, avnuSupportedTokens: AVNUTokenConfig) {
  if (!walletAddress) {
    return {};
  }

  const allTokensToUpdate = new Map<string, TokenToUpdateData>();

  // Get tokens supported by AVNU
  Object.entries(avnuSupportedTokens).map(([, tokenConfig]) => {
    allTokensToUpdate.set(addAddressPadding(tokenConfig.address), {
      address: addAddressPadding(tokenConfig.address),
      decimals: tokenConfig.decimals,
    });
  });

  const config = getConfigManager().getConfig();

  // Get tokens supported by lend&borrow
  Object.entries(config.assets).map(([, assetConfig]) => {
    allTokensToUpdate.set(addAddressPadding(assetConfig.address), {
      address: addAddressPadding(assetConfig.address),
      decimals: assetConfig.tokenPrecision,
    });
  });

  // Get token supported by pools
  Object.entries(config.pools).map(([, poolConfig]) => {
    allTokensToUpdate.set(addAddressPadding(poolConfig.pair.tokenA.address), {
      address: addAddressPadding(poolConfig.pair.tokenA.address),
      decimals: poolConfig.pair.tokenA.decimals,
    });

    allTokensToUpdate.set(addAddressPadding(poolConfig.pair.tokenB.address), {
      address: addAddressPadding(poolConfig.pair.tokenB.address),
      decimals: poolConfig.pair.tokenB.decimals,
    });
  });

  // Note: UNO, STRK and nstSTRK are covered by pool and lend&borrow tokens

  return fetchWalletTokenBalances(provider, Array.from(allTokensToUpdate.values()), walletAddress);
}

async function fetchWalletTokenBalances(
  provider: RpcProvider,
  tokensToUpdate: TokenToUpdateData[],
  walletAddress: Nullable<string>,
) {
  const calldata = tokensToUpdate.map(
    tokenToUpdate =>
      ({
        contractAddress: tokenToUpdate.address,
        entrypoint: 'balanceOf',
        calldata: [walletAddress],
      } as Call),
  );

  const multicall = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

  const result = await multicall.aggregateUint256ArrayAsync(calldata);
  if (!result) {
    return {};
  }

  const walletTokenBalancesMap: { [tokenAddress: string]: Decimal } = {};

  tokensToUpdate.forEach((tokenToUpdate, index) => {
    const tokenBalanceUint256 = result[index];

    walletTokenBalancesMap[tokenToUpdate.address] = tokenBalanceUint256
      ? new Decimal(tokenBalanceUint256, tokenToUpdate.decimals)
      : new Decimal(0);
  });

  return walletTokenBalancesMap;
}

export default walletTokenBalancesAtom;
